import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getAllSalesUsers,
  getAllActiveSalesUsersByProjectId,
  getMyData,
  updateLeadById
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';

const UpdateSalesUser = ({ onClose, leadId, currentSalesId, onSuccess, projectId }) => {
  const userId = parseInt(Cookies.get('userId'), 10);
  const userVertical = Cookies.get('userVertical');
  const userLevel = Cookies.get('userDesignationLevel');
  const isSalesVertical = userVertical === 'Sales';
  const isSalesExecutive = isSalesVertical && userLevel === 'Low';
  const isSalesManager = isSalesVertical && userLevel === 'Medium';
  const projectIdNum =
    projectId != null && projectId !== '' ? parseInt(projectId, 10) : null;
  const hasProjectScope = Number.isFinite(projectIdNum) && projectIdNum > 0;

  const { register, handleSubmit } = useForm({
    defaultValues: {
      sales_id: currentSalesId?.toString() || ''
    }
  });

  // Project-scoped when we know the lead's project; otherwise company Sales list
  const { data: salesUsers } = useQuery({
    queryKey: ['assignableSalesUsers', hasProjectScope ? projectIdNum : 'all'],
    queryFn: () =>
      hasProjectScope
        ? getAllActiveSalesUsersByProjectId(projectIdNum)
        : getAllSalesUsers(1, 1000)
  });

  // Managers need full profile for UserManager reportees (list API omits manager relations)
  const { data: meFull } = useQuery({
    queryKey: ['meFullForLeadAssign'],
    queryFn: () => getMyData({ full: true }),
    enabled: isSalesManager
  });

  const assignableUsers = React.useMemo(() => {
    const list = (salesUsers?.data || []).filter((user) => user?.is_active);
    const byId = new Map(list.map((user) => [user.id, user]));

    const ensureUnique = (users) => {
      const seen = new Set();
      return users.filter((user) => {
        if (!user?.id || seen.has(user.id)) return false;
        seen.add(user.id);
        return true;
      });
    };

    // Sales executive: fellow executives in project/company scope
    if (isSalesExecutive) {
      return ensureUnique(list.filter((user) => user.level === 'Low'));
    }

    // Sales manager: self + reportees for this project (fallback: Low on project)
    if (isSalesManager) {
      const managerEntries = meFull?.manager || [];
      const forProject = hasProjectScope
        ? managerEntries.filter((m) => Number(m.project_id) === projectIdNum)
        : managerEntries;

      const reportees = forProject
        .map((m) => {
          const nested = m.user;
          if (nested?.id) {
            return byId.get(nested.id) || nested;
          }
          if (m.user_id) {
            return byId.get(m.user_id) || null;
          }
          return null;
        })
        .filter((user) => user && user.is_active !== false);

      const self = byId.get(userId) || meFull || null;
      const scoped = ensureUnique([self, ...reportees].filter(Boolean));

      if (scoped.length > 1) {
        return scoped;
      }

      // No UserManager rows yet — still allow assigning fellow executives on project
      return ensureUnique([
        self,
        ...list.filter((user) => user.level === 'Low' && user.id !== userId)
      ].filter(Boolean));
    }

    // Sales admin (High), Management, builder, etc.: all active Sales in scope
    return ensureUnique(list);
  }, [
    salesUsers?.data,
    meFull,
    userId,
    isSalesExecutive,
    isSalesManager,
    hasProjectScope,
    projectIdNum
  ]);

  const { mutate: updateLead, isLoading } = useMutation({
    mutationFn: (data) => updateLeadById(leadId, data),
    onSuccess: () => {
      toast.success('Sales user updated successfully');
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update sales user'
      );
    }
  });

  const onSubmit = (data) => {
    updateLead({ sales_id: parseInt(data.sales_id, 10) });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <FormInput
        width={100}
        label={'Assign To Sales User'}
        register={register}
        register_key={'sales_id'}
        type={'dropdown'}
        options={[
          { value: '', label: 'Select Sales User', disabled: true },
          ...assignableUsers.map((user) => ({
            value: user.id.toString(),
            label: user.name
          }))
        ]}
      />

      <div className="flex justify-end items-center space-x-4 mt-4">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          Update
        </Button>
      </div>
    </form>
  );
};

export default UpdateSalesUser;
